/**
 * react-query wiring for Today (pedagogy spec §6.3.4). The plan's key is
 * `["practice", "today", settingsHash]`, so an answer (which invalidates `["practice"]`) and a
 * change under "Anpassa" both refetch it; the runner's variant carries the problems
 * (`include=questions`) under its own leaf. The mixed-card counts share the flashcards feature's
 * `["flashcards", "decks"]` entry (the same endpoint Home already reads), without importing it.
 */
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { api } from "@client/api/client";
import { useAuth } from "@client/app/auth";
import { isCode } from "@client/lib/errors";
import {
  defaultSettingsFor,
  loadSettings,
  todayKey,
  loadSkipTopics,
  planQueryString,
  saveSettings,
  saveSkipTopics,
  settingsHash,
  withoutOverride,
  type TodaySettings,
} from "./settings";
import type { MixedCards, TodayPlanDto } from "./types";

/** The plan is cheap, but Home does not need to ask again within a minute. */
export const TODAY_STALE_TIME = 60_000;

export const todayKeys = {
  plan: (settings: TodaySettings, skip: readonly string[], questions: boolean) =>
    questions
      ? (["practice", "today", settingsHash(settings, skip), "questions"] as const)
      : (["practice", "today", settingsHash(settings, skip)] as const),
};

/** `GET /api/practice/today`; `questions` adds the problems and plan offers (the runner). */
export function useTodayPlan(
  settings: TodaySettings,
  skip: readonly string[],
  questions: boolean,
  enabled = true,
) {
  return useQuery({
    queryKey: todayKeys.plan(settings, skip, questions),
    queryFn: () =>
      api.get<TodayPlanDto>(`/api/practice/today?${planQueryString(settings, skip, questions)}`),
    staleTime: TODAY_STALE_TIME,
    // A change under "Anpassa" asks for a new plan; the old one stays on screen meanwhile.
    placeholderData: keepPreviousData,
    enabled,
  });
}

/** What the mixed review holds right now; undefined until `GET /decks` answers. */
export function useMixedCards() {
  return useQuery({
    queryKey: ["flashcards", "decks"] as const,
    queryFn: () => api.get<{ mixed?: MixedCards }>("/api/flashcards/decks"),
    select: (data) => data.mixed,
  });
}

/**
 * The settings, the skip list and the plan they ask for, kept together: Home's card and the
 * runner both read them from `localStorage` on mount and write them back on `apply`.
 */
export function useTodayData(options: { questions: boolean; enabled?: boolean }) {
  const { user } = useAuth();
  const prefs = user?.prefs;
  // The "Kom igång" answers give the defaults (slice C); a saved "Anpassa" choice wins.
  const [state, setState] = useState(() => ({
    settings: loadSettings(todayKey(), defaultSettingsFor(prefs)),
    skip: loadSkipTopics(),
  }));
  // Answers saved while Home is open (the Kom igång card above) apply at once.
  const minutes = prefs?.minutesPerDay;
  useEffect(() => {
    setState((current) => ({
      ...current,
      settings: loadSettings(todayKey(), defaultSettingsFor(prefs)),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only the time per day matters here
  }, [minutes]);
  const plan = useTodayPlan(state.settings, state.skip, options.questions, options.enabled ?? true);
  const mixed = useMixedCards();

  const apply = useCallback((settings: TodaySettings, skip: readonly string[]) => {
    saveSettings(settings);
    saveSkipTopics(skip);
    setState({ settings, skip: [...skip] });
  }, []);

  // A topic picked by hand that the server cannot start (no problems to serve) is forgotten,
  // so the plan comes back with the automatic pick instead of staying an error.
  const rejected = plan.isError && isCode(plan.error, "VALIDATION") && !!state.settings.newTopic;
  useEffect(() => {
    if (!rejected) return;
    apply(withoutOverride(state.settings), state.skip);
  }, [rejected, state, apply]);

  return { settings: state.settings, skip: state.skip, plan, mixed, apply };
}
